import { Box } from "@mui/material";
import { AxiosError, AxiosResponse } from "axios";
import { useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import ParkOutlinedIcon from "@mui/icons-material/ParkOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DashboardWrapper from "src/components/common/DashboardWrapper";
import {
  AutoComplete,
  Button,
  DateInput,
  FileUpload,
  Input,
} from "src/components/common/FormComponents";
import errorMessage from "src/constants/errorMessage";
import { path } from "src/constants/path";
import {
  getFundApplicationById,
  getLandDetailsById,
} from "src/utils/api/createProjectFlow";
import defaultStyles from "./styles";
import {
  EditFundApplicationAPI,
  fillFundApplicationAPI,
} from "src/utils/api/dashboard";
import { useEffect } from "react";
import { dateAndTimeStamp } from "src/utils/helper";
import {
  ActionFooter,
  DetailField,
  DetailFieldGrid,
  SectionCard,
  fundUi,
  installmentLabel,
  money,
} from "../ProjectDetailView/FundApplicationDetails/FundApplicationUI";

/** Vertical rhythm between the money inputs — matches the raise-FA form. */
const fieldStackSx = {
  display: "flex",
  flexDirection: "column",
  gap: "26px",
};

export type demandNoteFormData = {
  demandRecipt: {
    type: string;
    key: string;
    file: string;
  }[];
};

const ViewFundApplication = () => {
  const [searchParams] = useSearchParams();

  const paymentId = searchParams.get("paymentId");

  const navigate = useNavigate();

  const { isLoading, mutate } = useMutation(
    "fillFundApllicationForm",
    ({ paymentId, data }: { paymentId: string; data: any }) =>
      EditFundApplicationAPI(paymentId, data),
    {
      onSuccess: () => {
        navigate("/");
      },
      onError: (error: any) => {},
    }
  );

  const { data: landData } = useQuery(
    ["userDetails", paymentId],
    () => getFundApplicationById(paymentId),
    {
      onSuccess: (res: AxiosResponse) => {},
      onError: (req: AxiosError) => {},
      enabled: !!paymentId,
      refetchOnMount: true,
    }
  );

  useEffect(() => {
    if (!!landData) {
      reset({
        estimatedAdvanceWorkCost:
          landData?.data?.fundDetails?.estimatedAdvanceWorkCost,
        estimatedRaisingCost: landData?.data?.fundDetails?.estimatedRaisingCost,
        estimatedMaintenanceCost:
          landData?.data?.fundDetails?.estimatedMaintenanceCost,
        estimatedMonitoringCost:
          landData?.data?.fundDetails?.estimatedMonitoringCost,
        estimatedContingencyCost:
          landData?.data?.fundDetails?.estimatedContingencyCost,
        totalEstimatedCost: landData?.data?.fundDetails?.totalEstimatedCost,
        ecoRestorationCost: landData?.data?.fundDetails?.ecoRestorationCost,
        totalCost: landData?.data?.fundDetails?.totalCost,
        monitoringCost: landData?.data?.fundDetails?.monitoringCost,
        contingencyCost: landData?.data?.fundDetails?.contingencyCost,
        maintenanceCost: landData?.data?.fundDetails?.maintenanceCost,
        raisingCost: landData?.data?.fundDetails?.raisingCost,
        advanceWorkCost: landData?.data?.fundDetails?.advanceWorkCost,
        additionalInfo: landData?.data?.fundDetails?.additionalInfo,
      });
    }
  }, [landData]);

  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    watch,
    setError,
    clearErrors,
    trigger,
    reset,
    formState: { errors, isValid },
  } = useForm<any>({
    mode: "onChange",
    defaultValues: {
      estimatedAdvanceWorkCost: "",
      estimatedRaisingCost: "",
      estimatedMaintenanceCost: "",
      estimatedMonitoringCost: "",
      estimatedContingencyCost: "",
      totalEstimatedCost: "",
      advanceWorkCost: "",
      monitoringCost: "",
      contingencyCost: "",
      raisingCost: "",
      totalCost: "",
      maintenanceCost: "",
      query: "",
      additionalInfo: "",
    },
  });

  const estimatedAdvanceWorkCost = watch("estimatedAdvanceWorkCost");
  const estimatedRaisingCost = watch("estimatedRaisingCost");
  const estimatedMaintenanceCost = watch("estimatedMaintenanceCost");
  const estimatedMonitoringCost = watch("estimatedMonitoringCost");
  const estimatedContingencyCost = watch("estimatedContingencyCost");

  const advanceWorkCost = watch("advanceWorkCost");
  const raisingCost = watch("raisingCost");
  const maintenanceCost = watch("maintenanceCost");
  const monitoringCost = watch("monitoringCost");
  const contingencyCost = watch("contingencyCost");

  useEffect(() => {
    const totalCost =
      (parseFloat(estimatedAdvanceWorkCost) || 0) +
      (parseFloat(estimatedRaisingCost) || 0) +
      (parseFloat(estimatedMaintenanceCost) || 0) +
      (parseFloat(estimatedMonitoringCost) || 0) +
      (parseFloat(estimatedContingencyCost) || 0);

    setValue("totalEstimatedCost", totalCost.toFixed(2));
  }, [
    estimatedAdvanceWorkCost,
    estimatedRaisingCost,
    estimatedMaintenanceCost,
    estimatedMonitoringCost,
    estimatedContingencyCost,
    setValue,
  ]);

  useEffect(() => {
    const totalCost =
      (parseFloat(advanceWorkCost) || 0) +
      (parseFloat(raisingCost) || 0) +
      (parseFloat(maintenanceCost) || 0) +
      (parseFloat(monitoringCost) || 0) +
      (parseFloat(contingencyCost) || 0);

    setValue("totalCost", totalCost.toFixed(2));
  }, [
    advanceWorkCost,
    raisingCost,
    maintenanceCost,
    monitoringCost,
    contingencyCost,
    setValue,
  ]);

  const handleForm = (data: any) => {
    const excludeFields = ["query", "additionalInfo"];

    // Create a new object with numeric conversion for specific fields
    const numericData = Object.fromEntries(
      Object.entries(data).map(([key, value]) =>
        excludeFields.includes(key) ? [key, value] : [key, Number(value)]
      )
    );
    mutate({
      paymentId,
      data: numericData,
    });
  };

  /** One money input, styled like the raise-FA form's fields. */
  const amountField = (
    name: string,
    label: string,
    options?: { readOnly?: boolean }
  ) => (
    <Input
      name={name}
      label={label}
      placeholder={options?.readOnly ? "Calculated automatically" : label}
      control={control}
      errors={errors}
      rules={{ required: errorMessage.required }}
      readOnly={options?.readOnly}
      type="number"
      customStyles={{
        label: { fontSize: "13px" },
        input: options?.readOnly
          ? {
              backgroundColor: fundUi.softGreen,
              color: fundUi.deepGreen,
              fontWeight: 600,
            }
          : {},
      }}
    />
  );

  const fundDetails = landData?.data?.fundDetails;
  const landOwnership = fundDetails?.landOwnership;
  const totalArea = Number(landOwnership?.totalArea);

  return (
    <Box component="form" onSubmit={handleSubmit(handleForm)}>
      <DashboardWrapper
        title="Fund Application Form"
        customStyles={{
          children: {
            marginTop: "20px",
            padding: { xs: "16px", md: "22px" },
            paddingBottom: "40px",
            borderRadius: "18px",
            backgroundColor: "#F5FAF8",
          },
        }}
        showBackBtn
      >
        <Box>
          <Box sx={{ fontSize: "22px", fontWeight: 600, color: fundUi.value }}>
            Fund Application No : {fundDetails?.id ?? paymentId}
          </Box>
          {!!installmentLabel(fundDetails) && (
            <Box
              sx={{ fontSize: "14px", color: fundUi.label, marginTop: "6px" }}
            >
              {installmentLabel(fundDetails)}
            </Box>
          )}
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              lg: "repeat(2, minmax(0, 1fr))",
            },
            gap: "20px",
            alignItems: "stretch",
            marginTop: "20px",
          }}
        >
          <SectionCard
            title="Eco-Restoration Blocks Details"
            icon={<ParkOutlinedIcon />}
            accent
            fullWidth
          >
            <DetailFieldGrid columns={3}>
              <DetailField
                label="Entity Name"
                value={landOwnership?.user?.firstName}
                span
              />
              <DetailField
                label="Land Registration No."
                value={landOwnership?.projectDetail?.registration_no}
              />
              <DetailField
                label="Cost Estimate ID"
                value={fundDetails?.landOwnershipEpid}
              />
              <DetailField
                label="Eco-Restoration Amount"
                value={
                  landOwnership?.demandNote
                    ? money(landOwnership?.demandNote)
                    : undefined
                }
              />
              <DetailField
                label="State"
                value={landOwnership?.projectDetail?.address?.stateDetail?.name}
              />
              <DetailField
                label="District"
                value={
                  landOwnership?.projectDetail?.address?.districtDetail?.name
                }
              />
              <DetailField
                label="Division"
                value={
                  landOwnership?.projectDetail?.landSpecification?.division
                }
              />
              <DetailField
                label="Total Planned Area (Hectares)"
                value={landOwnership?.netPlantationArea}
              />
              <DetailField
                label="Land Area Requested (Hectares)"
                value={
                  Number.isFinite(totalArea) ? totalArea.toFixed(2) : undefined
                }
              />
            </DetailFieldGrid>
          </SectionCard>

          <SectionCard
            title="Estimated Amount For 1st Year (In Rupees)"
            icon={<InsightsOutlinedIcon />}
          >
            <Box sx={fieldStackSx}>
              {amountField(
                "estimatedAdvanceWorkCost",
                "Estimated Advance Work Cost"
              )}
              {amountField(
                "estimatedRaisingCost",
                "Estimated Raising/Creation Cost (If Any)"
              )}
              {amountField(
                "estimatedMaintenanceCost",
                "Estimated Maintenance Cost (If Any)"
              )}
              {amountField("estimatedMonitoringCost", "Mointoring Cost")}
              {amountField("estimatedContingencyCost", "Contingency Cost")}
              {amountField("totalEstimatedCost", "Total Cost", {
                readOnly: true,
              })}
            </Box>
          </SectionCard>

          <SectionCard
            title="Amount Required Now (In Rupees)"
            icon={<CurrencyRupeeIcon />}
          >
            <Box sx={fieldStackSx}>
              {amountField("advanceWorkCost", "Advance Work Cost")}
              {amountField("raisingCost", "Raising/Creation Cost")}
              {amountField("maintenanceCost", "Maintenance Cost")}
              {amountField("monitoringCost", "Mointoring Cost")}
              {amountField("contingencyCost", "Contingency Cost")}
              {amountField("totalCost", "Total Cost", { readOnly: true })}
            </Box>
          </SectionCard>

          <SectionCard
            title="Additional Information"
            icon={<InfoOutlinedIcon />}
            fullWidth
          >
            <Input
              name="additionalInfo"
              label="Additional Information"
              placeholder="(In 500 characters)"
              control={control}
              errors={errors}
              rules={{
                maxLength: {
                  value: 500,
                  message: "More then 500 characters are not allowed",
                },
              }}
              type="text"
              multiline
              minRows={6}
              customStyles={{
                label: { fontSize: "13px" },
                input: defaultStyles.multilineInput,
              }}
            />
          </SectionCard>

          {fundDetails?.queries?.length > 0 && (
            <SectionCard
              title="Fund Transfer Query"
              icon={<ForumOutlinedIcon />}
              fullWidth
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "repeat(2, minmax(0, 1fr))",
                  },
                  gap: "12px",
                }}
              >
                {fundDetails.queries.map((item) => (
                  <Box
                    key={item?.id ?? item?.created_at}
                    sx={{
                      padding: "12px 14px",
                      borderRadius: "12px",
                      border: `1px solid ${fundUi.border}`,
                      backgroundColor: "#FBFDFC",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: "10px",
                        fontSize: "12px",
                        color: fundUi.label,
                        marginBottom: "6px",
                      }}
                    >
                      <Box sx={{ fontWeight: 600 }}>
                        {item?.user?.firstName}
                      </Box>
                      <Box>{dateAndTimeStamp(item?.created_at, "IST")}</Box>
                    </Box>
                    <Box
                      sx={{
                        fontSize: "13px",
                        color: fundUi.value,
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {item?.query}
                    </Box>
                  </Box>
                ))}
              </Box>
            </SectionCard>
          )}

          <SectionCard
            title="Reply Query"
            icon={<DescriptionOutlinedIcon />}
            fullWidth
          >
            <Input
              name="query"
              label="Reply Query"
              control={control}
              errors={errors}
              multiline
              minRows={6}
              customStyles={{
                label: { fontSize: "13px" },
                input: {
                  height: "auto",
                  fontFamily: "Poppins",
                },
              }}
            />
          </SectionCard>
        </Box>

        <ActionFooter>
          <Button
            text="Submit"
            type="submit"
            disabled={!isValid}
            customStyles={{
              root: {
                fontSize: "16px",
              },
            }}
          />
        </ActionFooter>
      </DashboardWrapper>
    </Box>
  );
};

export default ViewFundApplication;
